import * as cx from 'classnames';
import compact from 'lodash-es/compact';
import debounce from 'lodash-es/debounce';
import isEqual from 'lodash-es/isEqual';
import union from 'lodash-es/union';
import without from 'lodash-es/without';
import { ActionButton, ACTIONS } from 'mk/forms/ActionButton';
import FieldWrapper from 'mk/forms/FieldWrapper';
import * as formApi from 'mk/forms/formApi';
import { Context } from 'mk/interfaces';
import Loadable from 'mk2/helpers/Loadable';
import * as React from 'react';

const ReactPlayer = Loadable({
    loader: () => import('react-player' /* webpackChunkName: "react-player" */),
    modules: ['react-player'],
    webpack: () => [ require.resolveWeak('react-player') ],
});

const REGEXP = /(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g;

interface Value {
    url: string;
    snippet: string;
}

interface OwnProps {
    context: Context;
    className?: string;
    name: string;
    initial?: Value;
    errors?: string[];
    required?: boolean;

    onChange(value: Value);
}

interface State {
    value: Value;
}

export class SnippetField extends React.Component<OwnProps, State> {

    public static defaultProps = {
        initial: null,
    };

    public checkForSnippets = debounce((message) => {
        // don't check for empty message here because it doesn't happen that often and we would need to specially handle last and canceled urls

        let match;
        let newUrls = [];

        do {
            match = REGEXP.exec(message);
            if (match) {
                newUrls.push(match[0]);
            }
        } while (match !== null);

        // don't check for empty newUrls here because we would need to specially handle last and canceled urls

        if (isEqual(this.lastUrls, newUrls)) { return; } // we already have results

        this.canceledUrls = without(this.canceledUrls, ...without(this.lastUrls, ...newUrls)); // uncancel canceled url when cleared from message
        this.lastUrls = newUrls;

        newUrls = without(newUrls, ...this.canceledUrls); // if url was already canceled once, we don't create snippet for it
        if (!newUrls.length) { return; }

        // post urls to server to get snippet data
        formApi.createSnippet(this.props.context.apiAuth, newUrls).then((response) => {
            const snippets = compact(response.body.snippets); // remove nulls
            if (snippets.length) { this.setSnippet(snippets[0]); }
        });
    }, 250);

    private lastUrls: string[] = [];
    private canceledUrls: string[] = [];

    public static isFormField() {
        return true;
    }

    constructor(props, context) {
        super(props, context);

        this.state = {
            value: props.initial,
        };
    }

    public clearSnippet = () => {
        // clear only value
        if (!this.state.value) {
            return;
        }

        this.canceledUrls = union(this.canceledUrls, [this.state.value.url]);
        this.setState({value: this.props.initial});

        if (this.props.onChange) {
            this.props.onChange(this.props.initial);
        }
    };

    public validate() {
        return [this.state.value ? this.state.value.snippet : this.props.initial, null];
    }

    public clear() {
        if (!this.hasValue()) {
            return;
        }

        this.canceledUrls = [];
        this.lastUrls = [];
        this.setState({value: this.props.initial});

        if (this.props.onChange) {
            this.props.onChange(this.props.initial);
        }
    }

    public hasValue() {
        return !!this.state.value;
    }

    public getValue() {
        return this.state.value;
    }

    public setValue(value) {
        this.setState({ value });
    }

    public render() {
        const {value} = this.state;
        return (
            <FieldWrapper
                className={cx('snippet-field', this.props.className)}
                errors={this.props.errors}
                name={this.props.name}
            >
                {value && (
                    <div className="preview-panel">
                        <div className="snippet-preview">
                            <ReactPlayer url={value.url} width={178} height={100} />
                            <ActionButton action={ACTIONS.CANCEL} onClick={this.clearSnippet} />
                        </div>
                    </div>
                )}
            </FieldWrapper>
        );
    }

    private setSnippet = (snippet) => {
        this.setState({value: snippet});

        if (this.props.onChange) {
            this.props.onChange(snippet);
        }
    };

}
